import type { FC } from 'react';

import HeroDraft from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/HeroDraft';
import Dota2Carousel from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/Dota2Carousel';
import InterGameBreak from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/InterGameBreak';
import { hasField } from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/utils';

import style from './style.module.scss';

interface Props {
  availableFields: Dota2AvailableField[];
  game: AggregatedGameDota2;
  team1: AggregatedTeamDota2;
  team2: AggregatedTeamDota2;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  // Inter-game break: when set, the "takes the victory! / Break" card floats
  // over the center carousel (the hero drafts stay visible on either side).
  breakWinner?: OpponentBase;
  breakWinnerIndex?: 0 | 1 | null;
}

// dota2 picks section, rendered below the score card (like the default
// NameBlock's Dota2LiveScoreboard owns the hero picks). Every sub-view is gated
// on the field that feeds it, not on the game's `detailed` flag: drafts need
// player_hero_id, the minimap needs tower_status, the gold curve needs
// net_worth. With a carousel to flank, the drafts run vertically on either side
// of it; on their own they sit side by side, horizontally.
const Dota2DetailedSection: FC<Props> = ({
  availableFields,
  game,
  team1,
  team2,
  opponent1,
  opponent2,
  breakWinner,
  breakWinnerIndex
}) => {
  const showHeroes = hasField(availableFields, 'player_hero_id');
  const showMap = hasField(availableFields, 'tower_status');
  const showGold = hasField(availableFields, 'net_worth');
  const showCarousel = showMap || showGold;

  const breakOverlay =
    breakWinner && (breakWinnerIndex === 0 || breakWinnerIndex === 1) ? (
      <InterGameBreak winnerName={breakWinner.name} winnerIndex={breakWinnerIndex} />
    ) : null;

  // No sub-view has data. The break card is still worth showing, but it is
  // absolutely positioned, so it needs a box of its own to cover.
  if (!showHeroes && !showCarousel) {
    if (!breakOverlay) return null;
    return <div className={style.breakOnly}>{breakOverlay}</div>;
  }

  // Drafts only (reached with showHeroes true) — no carousel to flank.
  if (!showCarousel) {
    return (
      <div className={style.basicDraft}>
        <HeroDraft heroes={team1.heroes} teamIndex={0} orientation="horizontal" />
        <HeroDraft heroes={team2.heroes} teamIndex={1} orientation="horizontal" />
        {breakOverlay}
      </div>
    );
  }

  return (
    <div className={style.detailed}>
      {showHeroes && (
        <div className={style.side}>
          <HeroDraft heroes={team1.heroes} teamIndex={0} orientation="vertical" />
        </div>
      )}
      <div className={style.center}>
        <Dota2Carousel
          game={game}
          team1={team1}
          team2={team2}
          opponent1={opponent1}
          opponent2={opponent2}
          showMap={showMap}
          showGold={showGold}
        />
      </div>
      {breakOverlay}
      {showHeroes && (
        <div className={style.side}>
          <HeroDraft heroes={team2.heroes} teamIndex={1} orientation="vertical" />
        </div>
      )}
    </div>
  );
};

Dota2DetailedSection.displayName = 'Dota2DetailedSection';

export default Dota2DetailedSection;
